import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

interface PageHeaderProps {
  /** 画面の性格。分析系とオペレーション系を取り違えないよう明示する（原則 11）。 */
  kind: "operations" | "analysis" | "data";
  title: string;
  description?: string;
  /** 期間フィルタ・自動更新など、この画面固有の操作 */
  controls?: ReactNode;
  /** 主要アクション（1 つに絞る） */
  action?: ReactNode;
}

const KIND_LABEL = {
  operations: "監視",
  analysis: "分析",
  data: "データ"
} as const;

export function PageHeader({ kind, title, description, controls, action }: PageHeaderProps) {
  return (
    <div className="sticky top-(--topbar-h) z-30 flex min-h-(--pagehead-h) flex-wrap items-center gap-x-4 gap-y-2 border-b border-line bg-surface px-4 py-2.5">
      <div className="flex min-w-0 items-baseline gap-2">
        <h1 className="truncate text-lg font-semibold tracking-tight text-fg">{title}</h1>
        <span className="eyebrow shrink-0">{KIND_LABEL[kind]}</span>
      </div>

      {description ? (
        <p className="hidden min-w-0 flex-1 truncate text-xs text-fg-subtle xl:block">
          {description}
        </p>
      ) : null}

      <div className="ml-auto flex shrink-0 flex-wrap items-center gap-2">
        {controls}
        {action}
      </div>
    </div>
  );
}

interface PageBodyProps {
  /**
   * 分析系は余白を取り最大幅を絞る、オペレーション系は幅を使い切って一望性を優先する。
   * 密度の方針を画面ごとに宣言させることで、混在を防ぐ（原則 11）。
   */
  density: "roomy" | "dense";
  children: ReactNode;
  className?: string;
}

export function PageBody({ density, children, className }: PageBodyProps) {
  return (
    <div
      className={cn(
        "min-w-0 flex-1",
        density === "roomy" ? "mx-auto w-full max-w-[1280px] p-6" : "p-4",
        className
      )}
    >
      {children}
    </div>
  );
}

interface PanelProps {
  title: string;
  /** 件数など、見出しに添える数値 */
  count?: ReactNode;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  /** 表を入れる場合はパディングを持たせない */
  flush?: boolean;
}

export function Panel({
  title,
  count,
  description,
  actions,
  children,
  className,
  flush = false
}: PanelProps) {
  return (
    <section className={cn("overflow-hidden rounded-lg border border-line bg-surface", className)}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line px-4 py-2.5">
        <h2 className="text-sm font-semibold text-fg">{title}</h2>
        {count !== undefined ? (
          <span className="num rounded-md bg-surface-sunken px-1.5 text-xs font-semibold text-fg-muted">
            {count}
          </span>
        ) : null}
        {description ? <p className="text-xs text-fg-subtle">{description}</p> : null}
        {actions ? <div className="ml-auto flex items-center gap-1.5">{actions}</div> : null}
      </div>
      <div className={flush ? undefined : "p-4"}>{children}</div>
    </section>
  );
}
